import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FixReport, NsfReversal, StatusFixReport } from '@lmc-app/core/services/payment-errors.service';

export interface RepairResultDialogData {
  scannedDate: string;
  idReport: FixReport;
  statusReport: StatusFixReport;
}

/**
 * What the repair did, and what it left for a person to do.
 *
 * A dialog rather than a banner because of the last part: an NSF already imported into NLS
 * cannot be reversed from this application, so every row moved off a return leaves work
 * nobody else will pick up. That list has to be acknowledged, and it has to be exportable —
 * whoever reverses it in NLS is working from a screen this page cannot reach.
 */
@Component({
  selector: 'app-repair-result-dialog',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule],
  templateUrl: './repair-result-dialog.component.html',
  styleUrls: ['./repair-result-dialog.component.scss'],
})
export class RepairResultDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: RepairResultDialogData,
    private readonly dialogRef: MatDialogRef<RepairResultDialogComponent>
  ) {}

  /** Every payment moved off a return. One flat list — the judgement is the reader's. */
  get reversals(): NsfReversal[] {
    return this.data.statusReport.nsfReversals;
  }

  close(): void {
    this.dialogRef.close();
  }

  /**
   * The manual work list as CSV.
   *
   * Keyed on the NLS payment-history row id, not on anything of ours: the person reversing
   * these is working in NLS, and that id is what identifies the record there.
   */
  exportReversals(): void {
    // Same columns and order as the cron's email — utils/payment-processing/
    // nsf-reversal-report.js on the backend. The two lists are worked by the same people
    // doing the same job, and two shapes of it is how they stop agreeing.
    const columns: { header: string; value: (r: NsfReversal) => unknown }[] = [
      { header: 'Loan number', value: (r) => r.loanNumber },
      { header: 'Due date', value: (r) => r.dueDate },
      { header: 'Amount', value: (r) => (r.amountInCents / 100).toFixed(2) },
      { header: 'Action', value: () => 'Check this payment in NLS and reverse the NSF if one is standing' },
      { header: 'Status was', value: (r) => r.statusWas },
      { header: 'Return code was', value: (r) => r.returnCodeWas },
      { header: 'Status now', value: (r) => r.statusNow },
      { header: 'NLS acctrefno', value: (r) => r.acctrefno },
      { header: 'NLS userdef01', value: (r) => r.nlsTransactionId },
      { header: 'KapCharge transaction id', value: (r) => r.kapchargeTransactionId },
      { header: 'Our transaction row id', value: (r) => r.transactionId },
    ];

    const lines = [
      columns.map((c) => this.csvCell(c.header)).join(','),
      // Every row, resolved or not. A list that quietly omitted the ones we could not tie to
      // a payment would understate the work, which is the bug this column exists to prevent.
      ...this.reversals.map((r) => columns.map((c) => this.csvCell(c.value(r))).join(',')),
    ];

    // BOM so Excel reads it as UTF-8 rather than mangling anything non-ASCII.
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nsf-to-check-${this.data.scannedDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * One CSV cell: quoted, with embedded quotes doubled. A leading =, +, - or @ is prefixed
   * with an apostrophe, because Excel would otherwise treat the value as a formula.
   */
  private csvCell(value: unknown): string {
    if (value === null || value === undefined) return '""';
    const text = String(value);
    const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  }
}
